import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const TeamContext = createContext(null);

export function useTeam() {
  const context = useContext(TeamContext);
  if (!context) {
    throw new Error('useTeam must be used within TeamProvider');
  }
  return context;
}

export function TeamProvider({ children }) {
  const { user, roles, isAuthenticated } = useAuth();
  const [teamMembers, setTeamMembers] = useState([]);
  const [teamRoles, setTeamRoles] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Extract team role from JWT roles
  const extractTeamRole = useCallback(() => {
    if (!roles || roles.length === 0) {
      return { isOwner: false, isAdmin: false, isMember: false, role: 'none' };
    }

    const isOwner = roles.some(r =>
      r === 'tenant.owner' ||
      r.startsWith('tenant.owner.') ||
      r === 'core.superadmin'
    );

    const isAdmin = isOwner || roles.some(r =>
      r === 'tenant.admin' ||
      r.startsWith('tenant.admin.') ||
      r === 'core.admin'
    );

    const isMember = isAdmin || roles.some(r =>
      r === 'tenant.member' ||
      r.startsWith('tenant.member.') ||
      r === 'user'
    );

    let role = 'none';
    if (isOwner) role = 'owner';
    else if (isAdmin) role = 'admin';
    else if (isMember) role = 'member';

    return { isOwner, isAdmin, isMember, role };
  }, [roles]);

  // Fetch team members
  const fetchTeamMembers = useCallback(async () => {
    if (!isAuthenticated) {
      setTeamMembers([]);
      setLoading(false);
      return;
    }

    try {
      const res = await fetch('/api/team/members', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTeamMembers(data.members || []);
      } else if (res.status === 403) {
        // User doesn't have team member access (not a team member)
        setTeamMembers([]);
      } else {
        throw new Error('Failed to fetch team members');
      }
    } catch (err) {
      console.error('Error fetching team members:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Fetch team roles from API
  const fetchTeamRoles = useCallback(async () => {
    if (!isAuthenticated) {
      setTeamRoles(null);
      return;
    }

    try {
      const res = await fetch('/api/team/roles', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setTeamRoles(data.roles);
      }
    } catch (err) {
      console.error('Error fetching team roles:', err);
    }
  }, [isAuthenticated]);

  // Sync team from Core (admin only)
  const syncTeam = async () => {
    try {
      const res = await fetch('/api/team/sync', {
        method: 'POST',
        credentials: 'include'
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to sync team');
      }

      const result = await res.json();
      // Refresh team members after sync
      await fetchTeamMembers();
      return result;
    } catch (err) {
      console.error('Error syncing team:', err);
      throw err;
    }
  };

  // Get a specific team member
  const getTeamMember = async (userId) => {
    try {
      const res = await fetch(`/api/team/member/${userId}`, { credentials: 'include' });
      if (res.ok) {
        return await res.json();
      }
      return null;
    } catch (err) {
      console.error('Error fetching team member:', err);
      return null;
    }
  };

  // Update team member role (owner only)
  const updateMemberRole = async (userId, role) => {
    try {
      const res = await fetch(`/api/team/member/${userId}/role`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role })
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to update role');
      }

      // Refresh team members
      await fetchTeamMembers();
      return await res.json();
    } catch (err) {
      console.error('Error updating member role:', err);
      throw err;
    }
  };

  // Find team member by ID
  const findMember = useCallback((userId) => {
    return teamMembers.find(m => m.user_id === userId);
  }, [teamMembers]);

  // Check if current user is specific member
  const isCurrentUser = useCallback((userId) => {
    return user?.id === userId;
  }, [user]);

  // Load team data on auth change
  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      Promise.all([fetchTeamMembers(), fetchTeamRoles()])
        .finally(() => setLoading(false));
    } else {
      setTeamMembers([]);
      setTeamRoles(null);
      setLoading(false);
    }
  }, [isAuthenticated, fetchTeamMembers, fetchTeamRoles]);

  // Computed team role (from local extraction, falls back to API)
  const computedRoles = teamRoles || extractTeamRole();

  const value = {
    // Team members
    teamMembers,
    loading,
    error,

    // Team roles
    teamRoles: computedRoles,
    isTeamOwner: computedRoles.isOwner,
    isTeamAdmin: computedRoles.isAdmin,
    isTeamMember: computedRoles.isMember,
    teamRole: computedRoles.role,

    // Actions
    fetchTeamMembers,
    syncTeam,
    getTeamMember,
    updateMemberRole,

    // Utilities
    findMember,
    isCurrentUser,

    // Current user info
    currentUserId: user?.id
  };

  return <TeamContext.Provider value={value}>{children}</TeamContext.Provider>;
}
